import {
  showError,
  showSuccess,
  extractErrorMessage,
} from "../errorservices/errorservices";
import {
  bulkPaymentsPayrollServiceEndPoints,
  paymentEndPoints,
} from "../urlConfig/apiEndpoints";
import type { ApiResponse } from "./ApiResponse";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import { getToken } from "@/app/services/context/tokenGetter";
import type {
  BulkDownloadTemplatePayload,
  BulkDownloadTemplateResponse,
  BulkFileUploadPayload,
  BulkPaymentsListFileApprovalPayload,
  BulkPaymentsListFileApprovalResponse,
  BulkPayrollFileApprovalPayload,
  BulkPayrollFileApprovalResponse,
  BulkFileUploadResponse,
  PayrollBulkTransactionByStatusPayload,
  PayrollBulkTransactionByStatusResponse,
  PayrollReviewTransactionViewSummaryResponse,
  LookupTypePayload,
  LookupTypeResponse,
  PaymentsBulkApprovalViewSummaryPayload,
  PaymentsBulkApprovalViewSummaryResponse,
  PaymentsBulkmoduleTypePayload,
  PaymentsBulkmoduleTypeResponse,
  PaymentsBulkRejectPayload,
  PaymentsBulkRejectResponse,
  PaymentsBulkReviewTransactionByStatusPayload,
  PaymentsBulkReviewTransactionByStatusResponse,
  PaymentsBulkReviewTransViewSummaryPayload,
  PaymentsBulkReviewTransViewSummaryResponse,
  PaymentsBulkSubmitPayload,
  PaymentsBulkSubmitResponse,
  PaymentsBulkTransactionDetailDownloadPayload,
  PaymentsBulkTransactionDetailDownloadResponse,
  PaymentsBulkTransactionDetailsPayload,
  PaymentsBulkTransactionDetailsResponse,
  PaymentsBulkViewSummaryPayload,
  PaymentsBulkViewSummaryResponse,
  PayrollBulkApprovalViewSummaryPayload,
  PayrollBulkApprovalViewSummaryResponse,
  PayrollDebitAccountPayload,
  PayrollDebitAccountResponse,
  PayrollRejectPayload,
  PayrollRejectResponse,
  PayrollSubmitPayload,
  PayrollSubmitResponse,
  PayrollTransactionDetailsPayload,
  PayrollTransactionDetailsResponse,
  PayrollViewSummaryPayload,
  PayrollViewSummaryResponse,
  ViewFileformatPayload,
  ViewFileformatResponse,
  PayrollReviewTransactionViewSummaryPayload,
  RetryBulkPaymentPayload,
  GxNonceApiPayload,
  GxNonceApiResponse,
} from "@/app/types/bulkPaymentsPayroll";
import { executeLogout } from "@/app/utils/loginFlow/logoutHelper";
import { store, type RootState } from "@/app/redux/store";
import type { DebitAccount, DebitAccountPayload } from "@/app/types/payments";
import { CORRELATION_ID_HEADER, generateCorrelationId } from "@/app/utils/loginFlow/correlationId";

const getTenantId = (): string | undefined => {
  const state = store.getState() as RootState;
  return state.userData?.data?.tenantId;
};

// ---------------- Auth logout registration ----------------
const handleUnauthorized = (err: any) => {
  executeLogout();
  return Promise.reject(err);
};

const handleApiRequest = async <T>(
  method: typeof axios.get | typeof axios.post | typeof axios.delete,
  url: string,
  payload: any = {},
  showSuccessMsg?: boolean,
): Promise<ApiResponse<T>> => {
  try {
    const token = getToken();
    const tenantId = getTenantId();
     const correlationId = generateCorrelationId();
    const headers: Record<string, string | undefined> = {
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
      appId: tenantId,
        [CORRELATION_ID_HEADER]: correlationId,
    };
    if (!(payload instanceof FormData))
      headers["Content-Type"] = "application/json";

    const config: AxiosRequestConfig = { headers };
    const response: AxiosResponse =
      method === axios.get || method === axios.delete
        ? await method(url, { ...config, params: payload })
        : await method(url, payload, config);

    const data: ApiResponse<T> = response.data;

    if (data?.status === 0) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 1) {
      if (showSuccessMsg && data.errorMessage) showSuccess(data.errorMessage);
      return data;
    }

    if (data?.status === 2) {
      showError(undefined, data.errorMessage || "Request failed.");
      throw new Error(data.errorMessage || "Request failed.");
    }

    throw new Error("Unexpected server response.");
  } catch (err: any) {
    const { code, message } = extractErrorMessage(err);

    if (code === 401) {
      return handleUnauthorized(err);
    }

    showError(code, message);
    return Promise.reject(err);
  }
};

// ---------------- Exported API GET methods ----------------
export const gxLookupTypeApi = (payload: LookupTypePayload) => {
  return handleApiRequest<LookupTypeResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxLookupType,
    payload,
  );
};

export const gxViewFileformatApi = (payload: ViewFileformatPayload) => {
  return handleApiRequest<ViewFileformatResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxViewFileformat,
    payload,
  );
};

export const gxBulkFileUploadApi = (payload: BulkFileUploadPayload) => {
  return handleApiRequest<BulkFileUploadResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxBulkFileUpload,
    payload,
  );
};

export const gxPayrollDebitAccountsApi = (
  payload: PayrollDebitAccountPayload,
) => {
  return handleApiRequest<PayrollDebitAccountResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollDebitAccounts,
    payload,
  );
};

export const gxPaymentsBulkViewSummaryApi = (
  payload: PaymentsBulkViewSummaryPayload,
) => {
  return handleApiRequest<PaymentsBulkViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkViewSummary,
    payload,
  );
};

export const gxPayrollViewSummaryApi = (payload: PayrollViewSummaryPayload) => {
  return handleApiRequest<PayrollViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollViewSummary,
    payload,
  );
};

export const gxBulkDownloadTemplateApi = (
  payload: BulkDownloadTemplatePayload,
) => {
  return handleApiRequest<BulkDownloadTemplateResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxBulkDownloadTemplate,
    payload,
  );
};

export const gxPaymentsBulkmoduleTypeApi = (
  payload: PaymentsBulkmoduleTypePayload,
) => {
  return handleApiRequest<PaymentsBulkmoduleTypeResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkmoduleType,
    payload,
  );
};

export const gxPaymentsBulkReviewTransactionViewSummaryApi = (
  payload: PaymentsBulkReviewTransViewSummaryPayload,
) => {
  return handleApiRequest<PaymentsBulkReviewTransViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkReviewTransactionViewSummary,
    payload,
  );
};

export const gxPaymentsBulkReviewTransactionByStatusApi = (
  payload: PaymentsBulkReviewTransactionByStatusPayload,
) => {
  return handleApiRequest<PaymentsBulkReviewTransactionByStatusResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkReviewTransactionByStatus,
    payload,
  );
};

export const gxPaymentsBulkTransactionDetailsApi = (
  payload: PaymentsBulkTransactionDetailsPayload,
) => {
  return handleApiRequest<PaymentsBulkTransactionDetailsResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkTransactionDetails,
    payload,
  );
};

export const gxPayrollReviewTransactionViewSummaryApi = (
  payload: PayrollReviewTransactionViewSummaryPayload,
) => {
  return handleApiRequest<PayrollReviewTransactionViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollReviewTransactionViewSummary,
    payload,
  );
};

export const gxPayrollBulkTransactionByStatusApi = (
  payload: PayrollBulkTransactionByStatusPayload,
) => {
  return handleApiRequest<PayrollBulkTransactionByStatusResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollBulkTransactionByStatus,
    payload,
  );
};

export const gxPayrollTransactionDetailsApi = (
  payload: PayrollTransactionDetailsPayload,
) => {
  return handleApiRequest<PayrollTransactionDetailsResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollTransactionDetails,
    payload,
  );
};

// ============================================================
export const gxPaymentsBulkSubmitApi = (payload: PaymentsBulkSubmitPayload) => {
  return handleApiRequest<PaymentsBulkSubmitResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkSubmit,
    payload,
  );
};

export const gxPayrollSubmitApi = (payload: PayrollSubmitPayload) => {
  return handleApiRequest<PayrollSubmitResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollSubmit,
    payload,
  );
};

export const gxPaymentsBulkRejectApi = (payload: PaymentsBulkRejectPayload) => {
  return handleApiRequest<PaymentsBulkRejectResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkReject,
    payload,
  );
};

export const gxPayrollRejectApi = (payload: PayrollRejectPayload) => {
  return handleApiRequest<PayrollRejectResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollReject,
    payload,
  );
};

export const gxPaymentsBulkTransactionDetailDownloadApi = (
  payload: PaymentsBulkTransactionDetailDownloadPayload,
) => {
  return handleApiRequest<PaymentsBulkTransactionDetailDownloadResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkTransactionDetailDownload,
    payload,
  );
};

/*.........Payment & Payroll Approvel Reject..........*/

export const gxPaymentsBulkApprovalViewSummaryApi = (
  payload: PaymentsBulkApprovalViewSummaryPayload,
) => {
  return handleApiRequest<PaymentsBulkApprovalViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPaymentsBulkApprovalViewSummary,
    payload,
  );
};

export const gxPayrollBulkApprovalViewSummaryApi = (
  payload: PayrollBulkApprovalViewSummaryPayload,
) => {
  return handleApiRequest<PayrollBulkApprovalViewSummaryResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxPayrollBulkApprovalViewSummary,
    payload,
  );
};

// ============================================================================================

export const gxBulkPayrollListFileApprovalApi = (
  payload: BulkPayrollFileApprovalPayload,
) => {
  return handleApiRequest<BulkPayrollFileApprovalResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxBulkPayrollListFileApproval,
    payload,
  );
};

export const gxBulkPaymentsListFileApprovalApi = (
  payload: BulkPaymentsListFileApprovalPayload,
) => {
  return handleApiRequest<BulkPaymentsListFileApprovalResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxBulkPaymentsListFileApproval,
    payload,
  );
};

export const gxPaymentDebitAccountsApi = (payload: DebitAccountPayload) => {
  return handleApiRequest<DebitAccount[]>(
    axios.post,
    paymentEndPoints.gxPaymentDebitAccounts,
    payload,
  );
};

export const GxNonceApi = (payload: GxNonceApiPayload) => {
  return handleApiRequest<GxNonceApiResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxNonce,
    payload,
  );
};

export const gxBulkFilesRetryApi = (payload: RetryBulkPaymentPayload) => {
  return handleApiRequest<BulkPaymentsListFileApprovalResponse>(
    axios.post,
    bulkPaymentsPayrollServiceEndPoints.gxBulkFilesRetry,
    payload,
  );
};
